import {
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useRef,
} from "react";

import { cloudFunctions, firebaseDB } from "firebase/client";

import { baseUrl, sortDescByProperty } from "helpers";

import { CompetitionContext } from "contexts/CompetitionContext";

import { AlertContext } from "contexts/AlertContext";

import { MessageType } from "model/dictionary";

// Estado de un premio para la votación (lo calcula la callable).
export const AwardVotingStatus = {
  OPEN: "open",
  NO_FINALISTS: "noFinalists",
  CLOSED: "closed",
};

/**
 * Estado de la votación del jurado en una edición (por default, la vigente;
 * las anteriores se eligen en el filtro y sólo se consultan): qué jurados ya
 * votaron en cada uno de sus premios y cuáles no. Todo sale de la callable
 * `getJudgesVotingStatus`, que devuelve si votó o no pero nunca a quién: el
 * voto guardado no pasa por el navegador.
 *
 * Un premio sólo cuenta como pendiente si todavía se puede votar (tiene
 * finalistas y no tiene ganador); los demás se muestran pero no se le
 * reclaman al jurado.
 */
export default function useJudgesVoting() {
  const { currentCompetition } = useContext(CompetitionContext);

  const { showAlert, showWaiting } = useContext(AlertContext);

  const [loading, setLoading] = useState(true);

  const [awards, setAwards] = useState([]);

  const [judges, setJudges] = useState([]);

  const [error, setError] = useState();

  const [competitions, setCompetitions] = useState([]);

  // La edición elegida en el filtro. Arranca vacía y, mientras lo esté, se usa
  // la vigente (la del año en curso, de `CompetitionContext`) o, si todavía
  // no se creó, la más nueva.
  const [selectedId, setSelectedId] = useState("");

  const currentCompetitionId = currentCompetition && currentCompetition.id;

  const competitionId =
    selectedId ||
    currentCompetitionId ||
    (competitions[0] && competitions[0].value);

  // Los recordatorios sólo tienen sentido en la edición vigente: el link del
  // mail lleva a la pantalla de Votación, que vota siempre en esa.
  const isCurrent =
    Boolean(competitionId) && competitionId === currentCompetitionId;

  useEffect(() => {
    firebaseDB
      .collection("competitions")
      .get()
      .then(({ docs }) => {
        setCompetitions(
          docs
            .map((doc) => ({ value: doc.id, label: doc.data().title }))
            .sort(sortDescByProperty("label"))
        );

        return docs;
      })
      .catch((e) => {
        console.log(e);
      });
  }, []);

  // Si se cambia de edición antes de que vuelva la anterior, la respuesta
  // vieja no pisa a la nueva.
  const lastRequest = useRef();

  const load = useCallback(async () => {
    if (!competitionId) {
      return;
    }

    const request = {};
    lastRequest.current = request;

    setLoading(true);
    setError();

    try {
      const getStatus = cloudFunctions.httpsCallable("getJudgesVotingStatus");

      const { data } = await getStatus({ competitionId });

      if (lastRequest.current !== request) {
        return;
      }

      setAwards(data.awards);
      setJudges(data.judges);
    } catch (e) {
      console.log(e);

      if (lastRequest.current === request) {
        setError("No se pudo obtener el estado de la votación.");
      }
    } finally {
      if (lastRequest.current === request) {
        setLoading(false);
      }
    }
  }, [competitionId]);

  useEffect(() => {
    load();
  }, [load]);

  const awardsById = useMemo(() => {
    const byId = {};

    awards.forEach((award) => {
      byId[award.id] = award;
    });

    return byId;
  }, [awards]);

  // Cada jurado con sus premios ya resueltos (título, temática, estado) y la
  // cuenta de lo que tiene pendiente.
  const rows = useMemo(
    () =>
      judges.map((judge) => {
        const judgeAwards = judge.awards
          .filter((award) => awardsById[award.id])
          .map((award) => ({ ...awardsById[award.id], voted: award.voted }));

        const votable = judgeAwards.filter(
          (award) => award.status === AwardVotingStatus.OPEN
        );

        const pending = votable.filter((award) => !award.voted);

        return {
          ...judge,
          awards: judgeAwards,
          votableCount: votable.length,
          votedCount: votable.length - pending.length,
          pending,
        };
      }),
    [judges, awardsById]
  );

  const sendOne = useCallback(
    async (uid) => {
      const sendReminder = cloudFunctions.httpsCallable(
        "sendVotingReminderEmail"
      );

      const { data } = await sendReminder({
        competitionId,
        uid,
        url: `${baseUrl()}/jurado/votacion`,
      });

      setJudges((prev) =>
        prev.map((judge) =>
          judge.uid === uid ? { ...judge, lastReminderAt: data.sentAt } : judge
        )
      );
    },
    [competitionId]
  );

  const sendReminder = useCallback(
    async (judge) => {
      showWaiting(`Enviando el recordatorio a ${judge.name}...`);

      try {
        await sendOne(judge.uid);

        showAlert(
          MessageType.SUCESSFULL,
          `Recordatorio enviado a ${judge.email}`,
          2500
        );
      } catch (e) {
        console.log(e);

        showAlert(
          MessageType.DANGER,
          `No se pudo enviar el recordatorio: ${e.message}`,
          4000
        );
      }
    },
    [sendOne, showAlert, showWaiting]
  );

  return {
    loading,
    error,
    rows,
    competitions,
    competitionId,
    selectCompetition: setSelectedId,
    isCurrent,
    reload: load,
    sendReminder,
  };
}
